import React, { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Box, Card, CardContent, useTheme } from "@mui/material";
import FindContactInfo from "../../components/FindContact/FindContactInfo";
import { useLocation } from "react-router-dom";
import { FindContact } from "../../components/FindContact";
import { CreateContact } from "../../components/CreateContact/CreateContact";
import SwitcherComponent from "../../components/SwitcherComponent";
import { ContactSelectSuccess } from "../ContactSelect/ContactSelectSuccess";
import { ContactDetails } from "../../types/zoho";

const ASIDE_TRANSITION_MS = 360;

const AssignContact: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fullName = queryParams.get("fullName") ?? "";
  const email = queryParams.get("email") ?? "";

  const [isCreateSuccess, setIsCreateSuccess] = useState(false);
  const [isUpdateSuccess, setIsUpdateSuccess] = useState(false);

  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(
    null
  );

  const onUpdateSuccess = ({
    fullName,
    account,
  }: {
    fullName: string;
    account: string;
  }) => {
    setContactDetails({ fullName, account });
    setIsUpdateSuccess(true);
  };

  const onCreateSuccess = ({
    fullName,
    account,
  }: {
    fullName: string;
    account: string;
  }) => {
    setContactDetails({ fullName, account });
    setIsCreateSuccess(true);
  };

  const tabs = [
    { value: "search", label: "Search" },
    { value: "create", label: "Create New" },
  ];

  const [activeAssignTab, setActiveAssignTab] = useState<string>(tabs[0].value);
  const [createSimilarAside, setCreateSimilarAside] = useState<ReactNode | null>(null);
  const [similarAsidePanel, setSimilarAsidePanel] = useState<{
    open: boolean;
    node: ReactNode | null;
  }>({ open: false, node: null });

  useLayoutEffect(() => {
    if (createSimilarAside) {
      setSimilarAsidePanel({ open: true, node: createSimilarAside });
      return;
    }
    setSimilarAsidePanel((p) => ({ ...p, open: false }));
    const t = window.setTimeout(() => {
      setSimilarAsidePanel({ open: false, node: null });
    }, ASIDE_TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [createSimilarAside]);

  const leftColumnRef = useRef<HTMLDivElement | null>(null);
  const [leftColumnHeightPx, setLeftColumnHeightPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = leftColumnRef.current;
    if (!el) {
      setLeftColumnHeightPx(null);
      return;
    }
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      setLeftColumnHeightPx(h > 0 ? h : null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isCreateSuccess, isUpdateSuccess, activeAssignTab, createSimilarAside]);

  const asideExpandedLayout =
    activeAssignTab === "create" &&
    (similarAsidePanel.open || createSimilarAside != null);

  const showSimilarAsideSlot =
    activeAssignTab === "create" &&
    (similarAsidePanel.node != null ||
      similarAsidePanel.open ||
      createSimilarAside != null);

  const asideSlotContent = similarAsidePanel.node ?? createSimilarAside;

  const handleAssignTabChange = (value: string) => {
    setActiveAssignTab(value);
    if (value !== "create") {
      setCreateSimilarAside(null);
    }
  };

  const tabContents = {
    search: <FindContact onSuccess={onUpdateSuccess} />,
    create: (
      <CreateContact
        onSuccess={onCreateSuccess}
        onSimilarAsideChange={setCreateSimilarAside}
      />
    ),
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        flexWrap: { xs: "wrap", md: "nowrap" },
        gap: 2,
        justifyContent: "center",
        alignItems: "flex-start",
        width: "100%",
        maxWidth: {
          xs: "100%",
          md: asideExpandedLayout ? 960 : 520,
        },
        mx: "auto",
        px: { xs: 1, sm: 2 },
      }}>
      <Box
        ref={leftColumnRef}
        sx={{
          width: { xs: "100%", md: 520 },
          maxWidth: 520,
          flex: { xs: "0 0 auto", md: "0 0 520px" },
          flexShrink: { md: 0 },
          minWidth: { md: 520 },
          boxSizing: "border-box",
        }}>
        <Card
          elevation={5}
          sx={{
            width: "100%",
            transition: theme.transitions.create(
              ["box-shadow", "transform"],
              {
                duration: ASIDE_TRANSITION_MS,
                easing: theme.transitions.easing.easeInOut,
              }
            ),
            ...(activeAssignTab === "create" &&
              asideExpandedLayout && {
                boxShadow: theme.shadows[8],
              }),
          }}>
          <CardContent>
            {isCreateSuccess ? (
              <ContactSelectSuccess
                fullName={contactDetails?.fullName!}
                account={contactDetails?.account ?? null}
              />
            ) : isUpdateSuccess ? (
              <ContactSelectSuccess
                fullName={contactDetails?.fullName!}
                account={contactDetails?.account ?? null}
              />
            ) : (
              <>
                <FindContactInfo fullName={fullName} email={email} />
                <SwitcherComponent
                  tabs={tabs}
                  tabContents={tabContents}
                  onTabChange={handleAssignTabChange}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
      {showSimilarAsideSlot ? (
        <Box
          sx={{
            flex: { md: "0 0 auto" },
            flexShrink: { md: 0 },
            width: {
              xs: "100%",
              md: similarAsidePanel.open ? 340 : 0,
            },
            maxWidth: {
              xs: "100%",
              md: similarAsidePanel.open ? 340 : 0,
            },
            minWidth: 0,
            opacity: similarAsidePanel.open ? 1 : 0,
            alignSelf: "flex-start",
            overflow: "hidden",
            pointerEvents: similarAsidePanel.open ? "auto" : "none",
            transition: theme.transitions.create(
              ["width", "max-width", "opacity", "max-height"],
              {
                duration: ASIDE_TRANSITION_MS,
                easing: theme.transitions.easing.easeInOut,
              }
            ),
            maxHeight: {
              xs: similarAsidePanel.open ? "min(50vh, 400px)" : 0,
              md:
                leftColumnHeightPx != null && leftColumnHeightPx > 0
                  ? `${leftColumnHeightPx}px`
                  : "none",
            },
            height: {
              md:
                leftColumnHeightPx != null && leftColumnHeightPx > 0
                  ? `${leftColumnHeightPx}px`
                  : "auto",
            },
          }}>
          {asideSlotContent}
        </Box>
      ) : null}
    </Box>
  );
};

export default AssignContact;
